import React from 'react';

import { useAppSelector } from '@/store';

import type { GameSourceRecord } from './game-sources';
import { cachedGameRecords, fetchGamesByIds } from './games-api';

export interface UseGameRecords {
  /** The FULL records (art bundle included) of the asked ids that exist, in the order asked. An id the catalogue
   *  does not hold is absent. The same map from render to render while it holds the same records. */
  byId: ReadonlyMap<string, GameSourceRecord>;
  /** Some asked id is still being read (or there is no brand yet). False while disabled. */
  loading: boolean;
  /** The last read of these ids failed. The records that did come in stay in `byId`; `attempt` reads again. */
  failed: boolean;
}

const NO_RECORDS: ReadonlyMap<string, GameSourceRecord> = new Map();

/** The same records under the same ids, in the same order. */
const sameRecords = (a: ReadonlyMap<string, GameSourceRecord>, b: ReadonlyMap<string, GameSourceRecord>): boolean => {
  if (a.size !== b.size) return false;
  const other = b.entries();
  for (const [id, record] of a) {
    const next = other.next();
    if (next.done || next.value[0] !== id || next.value[1] !== record) return false;
  }
  return true;
};

/**
 * The FULL records of some games, by id: what the slim catalogue (`useGameSources`) leaves out, chiefly the art.
 * Read through `fetchGamesByIds`, so the ids every reader asks for in one render go out together, an id already held
 * or in flight is never read twice, and a remount paints from the cache at once.
 *
 * Keyed on the ids' content, not the array: a new array holding the same ids is the same ask (no new read, the same
 * `byId`). Re-renders when the records land. `enabled` false holds the read back (what is already held still shows);
 * `attempt` is a retry, as in `useGameSources`: changing it reads the ids again after a failure.
 */
export const useGameRecords = (ids: readonly string[], enabled = true, attempt = 0): UseGameRecords => {
  // Select the RAW value and coerce outside the selector (see useGameSources: a NaN selector result re-renders on
  // every dispatch).
  const brandTenant = useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id);
  const tenantRaw = Number(brandTenant);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;

  const key = JSON.stringify(ids);
  const asked = React.useMemo((): readonly string[] => JSON.parse(key) as string[], [key]);
  /** This ask, as a failure is remembered against: a new brand, ids or attempt starts clean. */
  const ask = `${tenantId ?? ''}|${attempt}|${key}`;

  // Bumped when this reader's read settles, to render again; what it shows is read from the shared cache.
  const [, setLanded] = React.useState(0);
  const [failedAsk, setFailedAsk] = React.useState<string | undefined>(undefined);

  // Read from the shared cache on every render, so records another reader brought in show at once, but handed out
  // as the SAME map while it holds the same records, so a consumer's memo or effect keyed on it does not run again.
  const known = tenantId === undefined ? undefined : cachedGameRecords(tenantId, asked);
  const held = React.useRef<ReadonlyMap<string, GameSourceRecord>>(NO_RECORDS);
  const current = known?.byId ?? NO_RECORDS;
  if (!sameRecords(held.current, current)) held.current = current;
  const complete = known?.complete ?? false;

  React.useEffect(() => {
    if (!enabled || tenantId === undefined || asked.length === 0) return undefined;
    if (cachedGameRecords(tenantId, asked).complete) {
      // Another reader's read landed between this render and this effect: nothing is left to read, but this reader
      // rendered without them, so it renders again (else it shows loading, and a memoized tile no art, until something
      // else re-renders it).
      if (!complete) setLanded((n) => n + 1);
      return undefined;
    }
    let stale = false;
    fetchGamesByIds(tenantId, asked).then(
      () => {
        if (stale) return;
        setFailedAsk(undefined);
        setLanded((n) => n + 1);
      },
      () => {
        if (stale) return;
        setFailedAsk(ask);
        setLanded((n) => n + 1);
      },
    );
    return () => {
      stale = true;
    };
  }, [enabled, tenantId, asked, ask, complete]);

  // A failure only stands while something is still unknown: records another reader brought in since answer it.
  const failed = enabled && failedAsk === ask && !complete;
  const loading = enabled && asked.length > 0 && !failed && !complete;
  return { byId: held.current, loading, failed };
};

export default useGameRecords;
